// ================================================================
// PROPRIETARY & CONFIDENTIAL — Mt Forge Inc.
// Copyright (c) 2026 Mt Forge. All rights reserved.
// Unauthorized copying, reverse engineering, or AI-assisted
// reproduction of this software is strictly prohibited.
// Protected under US Copyright Law (17 U.S.C.) and international
// treaties. Patent Pending. License: PROPRIETARY
// ================================================================
import React, { createContext, useContext, useState, useEffect } from 'react';
import { supabase } from './supabase';

// ═══════════════════════════════════════════════════════════════
// MT FORGE ALLOWED USERS — Only these emails can sign into MT Forge
// Anyone not on this list gets signed out immediately after auth
// ═══════════════════════════════════════════════════════════════
const ALLOWED_EMAILS = [
  "rmiskulin@gmail.com",
  "r.miskulin@miskulintechnologies.com",
  "criodtech@gmail.com",
  "j.lutz@miskulintechnologies.com",
  "tyler.hodgson32@gmail.com",
  "threewayindustries@gmail.com",
  "desbachynski@gmail.com",
  "lucas.s.mason@gmail.com",
  "leemehling@hotmail.com",
  "cloricer77@gmail.com",
  "tyson@canstorr.com",
  "jhansen860@yahoo.com",
  "littlesderek@gmail.com",
  "laken.weston@live.ca",
  "che@rosebudcannabisfarms.ca",
  "shanon@lanewaymedia.ca",
  "amandakristaleigh@hotmail.com",
  "demo@iqapps.dev",
  "r.miskulin@mtforge.ai",
  "t.hodgson@miskulintechnologies.com",
  "t.schenher@miskulintechnologies.com",
  "d.bachynski@miskulintechnologies.com",
  "l.mason@miskulintechnologies.com",
  "l.mehling@miskulintechnologies.com",
  "c.robertson@miskulintechnologies.com",
  "t.becker@miskulintechnologies.com",
  "h.blahut@miskulintechnologies.com",
  "dblahut@sastel.net",
  "d.blahut@miskulintechnologies.com",
  "a.benesocky@miskulintechnologies.com",
  "j.hansen@miskulintechnologies.com",
  "d.little@miskulintechnologies.com",
  "l.weston@miskulintechnologies.com",
  "c.leblanc@miskulintechnologies.com",
  "s.lutz@miskulintechnologies.com",
  "a.henwood@miskulintechnologies.com",
  "j.lutz@mtforge.ai",
  "t.schenher@mtforge.ai",
  "c.leblanc@mtforge.ai",
  "d.bachynski@mtforge.ai",
].map(e => e.toLowerCase());

const AuthContext = createContext({});

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [onboardingStep, setOnboardingStep] = useState(null);

  useEffect(() => {
    supabase.auth.getSession().then(({ data: { session } }) => {
      if (session?.user) {
        loadProfile(session.user);
      } else {
        setLoading(false);
      }
    });

    const { data: { subscription } } = supabase.auth.onAuthStateChange((_event, session) => {
      if (session?.user) {
        loadProfile(session.user);
      } else {
        setUser(null);
        setLoading(false);
      }
    });

    return () => subscription.unsubscribe();
  }, []);

  const loadProfile = async (authUser) => {
    try {
      let { data, error } = await supabase
        .from('users')
        .select('*')
        .eq('auth_user_id', authUser.id)
        .single();
      if (error || !data) {
        const res = await supabase
          .from('users')
          .select('*')
          .eq('email', authUser.email)
          .single();
        data = res.data;
      }
      const profile = data || { email: authUser.email, full_name: authUser.email.split('@')[0] };
      setUser(profile);
      const pwChanged = authUser?.user_metadata?.password_changed || localStorage.getItem('miskulinteam_password_changed');
      const ndaSigned = authUser?.user_metadata?.nda_signed_at || profile.nda_signed_at || localStorage.getItem('miskulinteam_nda_signed');
      const tutorialDone = localStorage.getItem('miskulinteam_tutorial_done');
      if (!pwChanged) setOnboardingStep('password');
      else if (!ndaSigned) setOnboardingStep('nda');
      else if (!tutorialDone) setOnboardingStep('tutorial');
      else setOnboardingStep(null);
    } catch (err) {
      // ── SECURITY: Profile load failure blocks access ──
      // Don't silently grant access with a minimal profile.
      // Sign out and force re-authentication.
      console.error('Profile load failed:', err.message);
      await supabase.auth.signOut();
      setUser(null);
      setOnboardingStep(null);
    } finally {
      setLoading(false);
    }
  };

  const signIn = async (email, password) => {
    const { data, error } = await supabase.auth.signInWithPassword({ email: email.trim().toLowerCase(), password });
    if (error) throw error;
    // ── ALLOWLIST CHECK: Block unauthorized users immediately ──
    const userEmail = (data?.user?.email || email).trim().toLowerCase();
    if (!ALLOWED_EMAILS.includes(userEmail)) {
      await supabase.auth.signOut();
      throw new Error('Access denied. This account is not authorized for MT Forge.');
    }
    return data;
  };

  const signUp = async (email, password, fullName) => {
    const { data, error } = await supabase.auth.signUp({
      email,
      password,
      options: { data: { full_name: fullName } }
    });
    if (error) throw error;
    return data;
  };

  const completeOnboardingStep = async (step) => {
    if (step === 'password') { localStorage.setItem('miskulinteam_password_changed', 'true'); try { await supabase.auth.updateUser({ data: { password_changed: true } }); } catch(e) { /* silent */ } setOnboardingStep('nda'); }
    else if (step === 'nda') { localStorage.setItem('miskulinteam_nda_signed', 'true'); setOnboardingStep('tutorial'); }
    else if (step === 'tutorial') { localStorage.setItem('miskulinteam_tutorial_done', 'true'); setOnboardingStep(null); }
  };
  const signOut = async () => {
    await supabase.auth.signOut();
    setUser(null);
    setOnboardingStep(null);
  };

  return (
    <AuthContext.Provider value={{ user, loading, signIn, signUp, signOut , onboardingStep, completeOnboardingStep }}>
      {children}
    </AuthContext.Provider>
  );
}

export const useAuth = () => useContext(AuthContext);
